import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { createSubscribe } from '../Redux/Slice/subscribe.slice';
import { motion, useInView } from 'framer-motion';

const SubscribeCTA = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.subscribe);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.3 }); // Only animate once when 30% visible

    const formik = useFormik({
        initialValues: {
            email: '',
            agree: false,
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email('Invalid email address')
                .required('Please enter your email.'),
            agree: Yup.boolean()
                .oneOf([true], 'Please agree to the Privacy Policy and Terms & Conditions.'),
        }),
        onSubmit: async (values, { resetForm, setSubmitting, setStatus }) => {
            setStatus({ success: '', error: '' });
            try {
                const resultAction = await dispatch(createSubscribe({ email: values.email }));
                if (createSubscribe.fulfilled.match(resultAction) && resultAction.payload?.success) {
                    setStatus({ success: 'Thanks for subscribing!', error: '' });
                    resetForm();
                } else {
                    setStatus({ error: resultAction.payload?.message || 'Something went wrong.', success: '' });
                }
            } catch (err) {
                setStatus({ error: err?.message || 'Failed to subscribe.', success: '' });
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <section ref={ref} className="relative overflow-hidden py-10 xl:py-24 text-white px-4 sm:px-6 lg:px-8 ">
            <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative max-w-[100%] md:max-w-[80%] w-full mx-auto "
            >
                {/* Neon gradient frame */}
                <div className="rounded-2xl md:rounded-3xl p-[1px] bg-[length:200%_200%] animate-gradient-x ">
                    {/* Background border shadow effect */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-2xl blur opacity-60" />
                    <div className="relative rounded-2xl md:rounded-3xl border border-white/10  backdrop-blur-md ">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 md:gap-12 items-center p-6 md600:p-10 bg-[#1b1920]/90 rounded-[25px]">
                            {/* Left: copy */}
                            <div>
                                <p className="inline-flex items-center gap-2 text-[10px] sm:text-xs tracking-widest uppercase text-white/70">
                                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_12px_rgba(255,100,0,0.6)]" />
                                    News, drops & beta access
                                </p>
                                <h2 className="mt-3 text-2xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
                                    Join the <span className="gradient-text">YOYO</span> Squad
                                </h2>
                                <p className="mt-4 sm:mt-6 text-gray-400 max-w-2xl text-sm sm:text-base">
                                    Receive special benefits, early access to new games, and unannounced discounts. Unsubscribe at any time - no spam.
                                </p>
                            </div>

                            {/* Right: form */}
                            <form onSubmit={formik.handleSubmit} className="w-full">
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col md600:flex-row items-stretch gap-3 md600:gap-4">
                                        <div className="relative w-full sm:flex-1">
                                            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm">@</span>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="your@email.com"
                                                className="w-full cursor-none sm:flex-1 min-w-0 rounded-lg bg-[#0f0f11] border border-neutral-800 pl-9 pr-4 py-3 outline-none focus:border-neutral-600 text-sm sm:text-base"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                aria-label="Email address"
                                                aria-invalid={formik.touched.email && Boolean(formik.errors.email)}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={formik.isSubmitting || loading}
                                            className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg bg-gradient-primary font-bold active:scale-[0.99] transition disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {formik.isSubmitting || loading ? 'Submitting...' : 'Subscribe'}
                                        </button>
                                    </div>
                                    {formik.touched.email && formik.errors.email ? (
                                        <p className="text-sm text-red-500" role="alert" aria-live="polite">{formik.errors.email}</p>
                                    ) : null}

                                    <label className="inline-flex items-center gap-3 text-xs md600:text-sm text-gray-400 select-none cursor-none">
                                        <span
                                            role="checkbox"
                                            aria-checked={formik.values.agree}
                                            tabIndex={0}
                                            onClick={() => formik.setFieldValue('agree', !formik.values.agree)}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ' || e.key === 'Enter') formik.setFieldValue('agree', !formik.values.agree);
                                            }}
                                            className={`h-4 w-4 rounded-full cursor-none border-2 ${formik.values.agree ? 'bg-primary border-primary' : 'border-primary'}`}
                                        />
                                        <span>
                                            I agree with{' '}
                                            <Link to="/privacyPolicy" className="text-[#c084fc] hover:text-[#c084fc]/80 transition-all  cursor-none duration-300">Privacy Policy</Link>
                                            {' '}and{' '}
                                            <Link to="/termsCondition" className="text-[#c084fc] hover:text-[#c084fc]/80  cursor-none transition-all duration-300">Terms & Conditions</Link>
                                        </span>
                                    </label>

                                    {/* Error and Success messages */}
                                    {formik.touched.agree && formik.errors.agree ? (
                                        <p className="text-sm text-red-500" role="alert" aria-live="polite">{formik.errors.agree}</p>
                                    ) : null}
                                    {formik.status?.error ? (
                                        <p className="text-sm text-red-500" role="alert" aria-live="polite">{formik.status.error}</p>
                                    ) : null}
                                    {formik.status?.success ? (
                                        <p className="text-sm text-green-500" role="status" aria-live="polite">{formik.status.success}</p>
                                    ) : null}
                                </div>
                            </form>
                        </div>

                        {/* Orbiting ring accent */}
                        <div
                            aria-hidden
                            className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full"
                            style={{
                                background:
                                    'conic-gradient(from 0deg, rgba(251,191,36,0.18), transparent 25%, rgba(236,72,153,0.2) 50%, transparent 75%, rgba(251,191,36,0.18))',
                                boxShadow: '0 0 20px rgba(251,191,36,0.15), inset 0 0 40px rgba(251,191,36,0.08)',
                                animation: 'spinSlow 22s linear infinite',
                                filter: 'blur(0.5px)'
                            }}
                        />
                    </div>
                </div>
            </motion.div>

            {/* local keyframes to match hero spin */}
            <style>{`@keyframes spinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </section>
    );
};

export default SubscribeCTA;


